import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { PageBreadcrumbComponent } from '../../../shared/components/common/page-breadcrumb/page-breadcrumb.component';
import { AuditApiService } from '../services/audit-api.service';
import { AuditAuthResponse, AuditAuthSearchParams } from '../models/audit.model';
import { AuthService } from '../../auth-pages/services/auth.service';
import {
  DirectoryUser,
  UserDirectoryApiService,
  directoryUserLabel,
} from '../../../shared/services/user-directory-api.service';

@Component({
  selector: 'app-audit-auth-logs',
  standalone: true,
  imports: [CommonModule, FormsModule, PageBreadcrumbComponent],
  templateUrl: './audit-auth-logs.component.html'
})
export class AuditAuthLogsComponent implements OnInit {
  logs: AuditAuthResponse[] = [];
  loading = false;
  error = '';

  // Pagination
  currentPage = 0;
  pageSize = 20;
  totalElements = 0;
  totalPages = 0;

  // Filters
  filterUsername = '';
  filterEventType = '';
  filterSuccess: '' | 'true' | 'false' = '';
  filterIpAddress = '';
  filterDateFrom = '';
  filterDateTo = '';
  showFilters = false;

  // Catálogo de usuarios para el dropdown
  users: DirectoryUser[] = [];
  loadingUsers = false;

  eventTypeOptions = ['LOGIN', 'LOGOUT', 'LOGIN_FAILED', 'TOKEN_REFRESH'];

  constructor(
    private auditApi: AuditApiService,
    private authService: AuthService,
    private userDirectory: UserDirectoryApiService
  ) {}

  ngOnInit(): void {
    this.loadUsers();
    this.loadLogs();
  }

  private loadUsers(): void {
    // Directorio de la empresa (negocio resuelto desde el token): a diferencia de
    // /api/auth/users/search no exige `users:VIEW`, que solo tienen Super Admin, Admin y Manager.
    this.loadingUsers = true;
    this.userDirectory.searchUsers('', 200).subscribe({
      next: data => {
        this.users = data;
        this.loadingUsers = false;
      },
      error: () => {
        this.loadingUsers = false;
      }
    });
  }

  userLabel(u: DirectoryUser): string {
    const name = [u.fullname, u.lastname].filter(Boolean).join(' ').trim();
    return name && u.mail ? `${name} (${u.mail})` : directoryUserLabel(u);
  }

  get activeFilterCount(): number {
    let count = 0;
    if (this.filterUsername) count++;
    if (this.filterEventType) count++;
    if (this.filterSuccess !== '') count++;
    if (this.filterIpAddress) count++;
    if (this.filterDateFrom) count++;
    if (this.filterDateTo) count++;
    return count;
  }

  loadLogs(page = 0): void {
    this.loading = true;
    this.error = '';
    this.currentPage = page;

    const params: AuditAuthSearchParams = {
      page,
      size: this.pageSize
    };
    if (this.filterUsername) params.username = this.filterUsername;
    if (this.filterEventType) params.eventType = this.filterEventType;
    if (this.filterSuccess !== '') params.success = this.filterSuccess === 'true';
    if (this.filterIpAddress) params.ipAddress = this.filterIpAddress;
    if (this.filterDateFrom) params.dateFrom = new Date(this.filterDateFrom).toISOString();
    if (this.filterDateTo) params.dateTo = new Date(this.filterDateTo).toISOString();

    this.auditApi.getAuthLogs(params).subscribe({
      next: data => {
        this.logs = data.content ?? [];
        this.currentPage = data.page ?? page;
        this.totalElements = data.totalElements ?? 0;
        this.totalPages = data.totalPages ?? 1;
        this.loading = false;
      },
      error: () => {
        this.error = 'Error al cargar los registros de autenticación.';
        this.loading = false;
      }
    });
  }

  onFilterChange(): void {
    this.loadLogs(0);
  }

  clearFilters(): void {
    this.filterUsername = '';
    this.filterEventType = '';
    this.filterSuccess = '';
    this.filterIpAddress = '';
    this.filterDateFrom = '';
    this.filterDateTo = '';
    this.loadLogs(0);
  }

  getEventTypeClass(eventType: string): string {
    switch (eventType) {
      case 'LOGIN': return 'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400';
      case 'LOGOUT': return 'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400';
      case 'LOGIN_FAILED': return 'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400';
      case 'TOKEN_REFRESH': return 'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400';
      default: return 'bg-gray-100 text-gray-600 dark:bg-gray-500/10 dark:text-gray-400';
    }
  }

  /**
   * Etiqueta del tipo de evento para una fila: prefiere `eventTypeLabel` del backend
   * (ya en español) y cae al switch local como respaldo (filas/valores sin label).
   */
  eventLabel(log: AuditAuthResponse): string {
    return log.eventTypeLabel ?? this.getEventTypeLabel(log.eventType);
  }

  /** Etiqueta del motivo de fallo: prefiere `failureReasonLabel`; cae al crudo; '—' si no hay. */
  failureReasonText(log: AuditAuthResponse): string {
    return log.failureReasonLabel ?? log.failureReason ?? '—';
  }

  /** Fallback cliente para el código crudo (filtros y filas sin label del backend). */
  getEventTypeLabel(eventType: string): string {
    switch (eventType) {
      case 'LOGIN': return 'Inicio de sesión';
      case 'LOGOUT': return 'Cierre de sesión';
      case 'LOGIN_FAILED': return 'Inicio de sesión fallido';
      case 'TOKEN_REFRESH': return 'Renovación de token';
      default: return eventType;
    }
  }
}
